import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './OtpVerification.css';

const OtpVerification = () => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputsRef = useRef([]);
  const navigate = useNavigate();

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const phoneNumber = localStorage.getItem('pharmacy_phone_number');
  const email = localStorage.getItem('pharmacy_email');
  const referral_code = localStorage.getItem('referral_code');

  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  // Cooldown countdown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleChange = (index, value) => {
    if (/^[0-9]?$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      if (value && index < 5) inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !otp[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const paste = event.clipboardData.getData('text').slice(0, 6).split('');
    if (paste.every((ch) => /^[0-9]$/.test(ch))) {
      setOtp((prev) =>
        prev.map((_, i) => (paste[i] !== undefined ? paste[i] : ''))
      );
      inputsRef.current[Math.min(paste.length, 5)]?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const enteredOtp = otp.join('');
    if (enteredOtp.length !== 6) {
      setError('Please enter all 6 digits of the OTP');
      return;
    }

    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append('otp_code', enteredOtp);
      formData.append('phone_number', phoneNumber);
      if (referral_code) formData.append('referral_code', referral_code);

      await axios.post(
        `${API_BASE_URL}/users/pharmacist-complete-registration/`,
        formData
      );

      setSuccess('✅ Verification successful! Redirecting...');
      localStorage.removeItem('pharmacy_phone_number');
      localStorage.removeItem('pharmacy_email');
      localStorage.removeItem('referral_code');

      setTimeout(() => navigate('/Login'), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Verification failed. Try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email || resendCooldown > 0 || resending) return;
    setResending(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('email', email);

      await axios.post(
        `${API_BASE_URL}/auth/resend-otp/pharmacist/`,
        formData
      );

      setSuccess('OTP resent! Check your email.');
      setResendCooldown(60); // 60 second cooldown
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resend OTP. Try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="otp-container">
      <form className="otp-form" onSubmit={handleSubmit}>
        <h2>🔐 OTP Verification</h2>

        {email && (
          <p className="otp-message">
            OTP sent to your email. Check junk if not in inbox: <strong>{email}</strong>
          </p>
        )}

        {error && <div className="otp-error">{error}</div>}
        {success && <div className="otp-success">{success}</div>}

        <div className="otp-inputs" onPaste={handlePaste}>
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputsRef.current[index] = el)}
              type="text"
              maxLength="1"
              className="otp-box"
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              disabled={isLoading}
            />
          ))}
        </div>

        <button type="submit" disabled={isLoading}>
          {isLoading ? 'Verifying...' : 'Verify OTP'}
        </button>

        <div style={{ marginTop: '1rem', textAlign: 'center' }}>
          <button
            type="button"
            onClick={handleResend}
            disabled={resending || resendCooldown > 0}
            style={{
              background: 'none',
              border: 'none',
              color: resendCooldown > 0 ? '#999' : '#007bff',
              cursor: resendCooldown > 0 ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem',
              textDecoration: resendCooldown > 0 ? 'none' : 'underline',
              padding: 0,
            }}
          >
            {resending
              ? 'Resending...'
              : resendCooldown > 0
              ? `Resend OTP in ${resendCooldown}s`
              : 'Resend OTP'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default OtpVerification;